import React, { useEffect, useRef } from "react"
import DOMPurify from "isomorphic-dompurify"
import { useInView } from "react-intersection-observer"
import { loadLazyImage } from "@/utils/lazyLoadImage"
import { convertStringToBoolean } from "@/utils/helper"
import {
  appendImageModifiers,
  getManualSmartCropBaseUrl,
  getManualSmartCropFallbackUrl,
  getManualSmartCropSrcSet,
  getSortedManualSmartCropItems,
  isManualSmartCropEnabled,
} from "@/components/core/Image/manualSmartCrop"

const CustomImage = props => {
  const { data } = props
  const {
    linkURL,
    newTab,
    fileReference,
    imageLazyLoad,
    defaultRenditions = "",
    imageModifiersScene7: authoredImageModifiersScene7 = "",
    defaultImageURL,
    imageURLWithoutCrop,
    imageServerUrl = "",
    assetID = "",
    imageResource = {},
    displayPopupTitle,
    imageComponentProperties: { alt, title },
    description,
    items = [],
    enableManualSmartCrop = false,
    smartCrops = [],
  } = data

  const pictureRef = useRef(null)
  const sourcesRef = useRef([])
  const imageRef = useRef(null)
  const { ref: inViewRef, inView } = useInView({
    threshold: 0,
  })
  const placeholderImage =
    "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
  const lazyLoadValue = convertStringToBoolean(imageLazyLoad) ? "false" : "true"
  const isManualSmartCrop = isManualSmartCropEnabled(enableManualSmartCrop)
  const manualSmartCropBaseUrl = getManualSmartCropBaseUrl({
    imageURLWithoutCrop,
    defaultImageURL,
    imageServerUrl,
    assetID,
    fileReference,
    imageResource,
  })
  const manualSmartCropSourceItems = items.length ? items : smartCrops
  const manualSmartCropItems = getSortedManualSmartCropItems(
    manualSmartCropSourceItems,
    manualSmartCropBaseUrl
  )
  const hasManualSmartCrops =
    isManualSmartCrop && manualSmartCropItems.length > 0
  const sourceItems = hasManualSmartCrops ? manualSmartCropItems : items
  const imageModifiersScene7 =
    authoredImageModifiersScene7 ||
    (hasManualSmartCrops ? "" : defaultRenditions)
  const finalSrcBaseValue = hasManualSmartCrops
    ? getManualSmartCropFallbackUrl({
        defaultImageURL,
        defaultRendition: defaultRenditions,
        baseUrl: manualSmartCropBaseUrl,
        sortedItems: manualSmartCropItems,
      })
    : defaultImageURL || imageURLWithoutCrop
  const finalSrcValue = appendImageModifiers(
    finalSrcBaseValue,
    imageModifiersScene7
  )
  const shouldLazyLoad =
    !hasManualSmartCrops &&
    convertStringToBoolean(imageLazyLoad) &&
    fileReference

  const content = shouldLazyLoad ? (
    <picture ref={pictureRef} lazyload={lazyLoadValue}>
      {sourceItems.map((obj, index) => {
        return (
          <source
            ref={el => (sourcesRef.current[index] = el)}
            key={index}
            media={`(min-width: ${obj.deviceSize})`}
            alt={alt || "true"}
            srcSet={placeholderImage}
            data-srcset={appendImageModifiers(
              obj.imageURL,
              imageModifiersScene7
            )}
          />
        )
      })}
      <img
        ref={imageRef}
        className="cmp-image__image"
        src={placeholderImage}
        data-src={finalSrcValue}
        alt={alt || "true"}
        style={{ width: "100%" }}
      />
    </picture>
  ) : (
    <picture lazyload={lazyLoadValue}>
      {sourceItems.map((obj, index) => {
        return (
          <source
            ref={el => (sourcesRef.current[index] = el)}
            key={index}
            media={`(min-width: ${obj.deviceSize})`}
            alt={alt || "true"}
            srcSet={
              hasManualSmartCrops
                ? getManualSmartCropSrcSet(obj.imageURL, {
                    modifiers: imageModifiersScene7,
                    deviceSize: obj.deviceSize,
                  })
                : appendImageModifiers(obj.imageURL, imageModifiersScene7)
            }
          />
        )
      })}

      <img
        ref={imageRef}
        className="cmp-image__image"
        src={finalSrcValue}
        srcSet={
          hasManualSmartCrops
            ? getManualSmartCropSrcSet(finalSrcBaseValue, {
                modifiers: imageModifiersScene7,
                deviceSize:
                  manualSmartCropItems[manualSmartCropItems.length - 1]
                    ?.deviceSize,
              })
            : undefined
        }
        alt={alt || "true"}
        style={{ width: "100%" }}
      />
    </picture>
  )
  useEffect(() => {
    if (inView) {
      if (shouldLazyLoad) {
        loadLazyImage(sourcesRef, imageRef)
      }
    }
  }, [inView, shouldLazyLoad])

  return (
    <div
      className="cmp-image"
      itemScope
      itemType="https://schema.org/ImageObject"
      ref={inViewRef}
    >
      {linkURL ? (
        <a
          className="cmp-image__link"
          href={linkURL}
          target={convertStringToBoolean(newTab) ? "_blank" : "_self"}
          rel="noreferrer"
        >
          {content}
        </a>
      ) : (
        content
      )}
      {!convertStringToBoolean(displayPopupTitle) && (
        <span
          className="cmp-image__title"
          itemProp="caption"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(description),
          }}
        />
      )}
      {convertStringToBoolean(displayPopupTitle) && title && (
        <meta itemProp="caption" content={title} />
      )}
    </div>
  )
}

export default CustomImage
